import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import MapPolygon from '../../MapObjects/MapPolygon';
import { Coordinate, CoordinateObject, PolygonCoordinates, Ring } from '../../../../types/geo';
import ConfirmActionPopover from '../../ConfirmPopovers/ConfirmActionPopover';
import { useLangFile } from '../../../context/LanguageContext';
import { setViewMode } from '../../../reducers/ControlReducer';
import ViewModeConstants from '../../../constants/ViewModeConstants';
import { Box, Divider, Typography } from '@mui/material';
import { getFieldsArray } from '../../ManageCrops/ManageCrops';
import { getFieldName } from '../../../helpers/StateInterpreters';
import * as turf from '@turf/turf';
import HelpItemContainer from '../../Help/HelpItemContainer';
import { MERGE_FIELDS_HELP_ARTICLE } from '../../../reducers/HelpReducer';
import areaCalculation from '../../../algorithms/geometry/AreaCalculation';
import { setIsEditingMergedField } from '../../../reducers/FieldReducer';
import MapFieldHole from '../../MapObjects/MapFieldHole';
import FieldTextCanvas from '../../ManageCrops/FieldTextCanvas';
import MergeFieldsInputDialog from './MergeFieldsInputDialog';

const mapStoreToProps = (store: any) => ({
  selectedField: store.field.selectedField,
  fields: store.field.fields,
  isEditingMergedField: store.field.isEditingMergedField,
  mergedFields: store.field.mergedFields,
});

type PropsFromRedux = ConnectedProps<typeof connector>;

const MergeFields: React.FC<PropsFromRedux> = ({
  selectedField,
  fields,
  isEditingMergedField,
  mergedFields,
  dispatch,
}) => {
  const LangFile = useLangFile();
  const [selectedFields, setSelectedFields] = useState([selectedField]);
  const submitButton = useRef({ disable: true, reason: '' });
  const [hoveredField, setHoveredField] = useState(null);
  const [showMergeFieldsInputDialog, setShowMergeFieldsInputDialog] = useState(false);

  const fieldsArray = getFieldsArray(fields);
  const fieldMapPolygons = fieldsArray.map((field, idx) => {
    if (
      isEditingMergedField &&
      mergedFields.fieldA.fieldId !== field.fieldId &&
      mergedFields.fieldB.fieldId !== field.fieldId
    ) {
      return null;
    }

    const coordinates: CoordinateObject[] = field.polygon.coordinates.map((ring: Ring) => {
      return ring.map((coord: Coordinate) => {
        return {
          lng: coord[0],
          lat: coord[1],
        };
      });
    });

    const visibleFieldHoles: PolygonCoordinates = field.polygon.coordinates.slice(1);

    const onMouseEnter = () => {
      setHoveredField(field);
    };

    const onMouseLeave = () => {
      setHoveredField(null);
    };

    const onClick = (clickedField): void => {
      if (!selectedFields.includes(clickedField)) {
        setSelectedFields([selectedField, clickedField]);
      } else if (clickedField !== selectedFields[0]) {
        setSelectedFields(selectedFields.slice(0, -1));
      }
    };

    let fillColor = 'white';
    let strokeColor = 'rgb(224,210,61)';
    let strokeWeight = 2;

    if (!isEditingMergedField) {
      if (selectedFields[0] === field) {
        fillColor = 'rgb(33, 150, 243)';
        strokeColor = '#2196F3';
        strokeWeight = 2;
      } else if (selectedFields[1] === field) {
        fillColor = 'rgb(255, 152, 0)';
        strokeColor = '#FF9800';
        strokeWeight = 2;
      }

      if (hoveredField === field) {
        strokeColor = 'white';
        strokeWeight = 6;
      }
    }

    return (
      <React.Fragment key={field.fieldId}>
        <MapPolygon
          key={idx}
          fillOpacity={selectedFields.includes(field) && !isEditingMergedField ? 0.4 : 0.0}
          fillColor={fillColor}
          strokeColor={strokeColor}
          strokeWeight={strokeWeight}
          disabled={field === selectedFields[0]}
          coordinates={coordinates}
          onClick={() => onClick(field)}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
        {visibleFieldHoles &&
          visibleFieldHoles.map((boundary, index) => (
            <MapFieldHole fieldHole={boundary} key={index} fieldHoleDisabled={true} />
          ))}
      </React.Fragment>
    );
  });

  const renderStatus = (): JSX.Element => {
    let mergedFieldsSize = 0.0;

    if (mergedFieldsPolygon) {
      const mergedPolygonsCoordinates: CoordinateObject[] =
        mergedFieldsPolygon.geometry.coordinates[0].map((coord: Coordinate): CoordinateObject => {
          return {
            lng: coord[0],
            lat: coord[1],
          };
        });

      mergedFieldsSize = areaCalculation({ coords: mergedPolygonsCoordinates }).size;
    }

    return (
      <>
        <Box>
          <Divider style={{ marginTop: '0', marginBottom: '8px', backgroundColor: '#ddd' }} />
          {[
            {
              label: getFieldName(selectedFields[0], LangFile) + ':',
              value: `${selectedFields[0].size.toFixed(2)} ha`,
            },
            {
              label: selectedFields[1]
                ? getFieldName(selectedFields[1], LangFile) + ':'
                : LangFile.MergeFieldConfirmationPopover.status.selectAFieldToMerge + ':',
              value: `${selectedFields[1] ? selectedFields[1].size.toFixed(2) : '0.00'} ha`,
              style: { marginBottom: '20px' },
            },
            {
              label: LangFile.MergeFieldConfirmationPopover.status.mergedFieldSize + ':',
              value: `${mergedFieldsSize.toFixed(2)} ha`,
            },
          ].map(({ label, value, style }, index) => (
            <Box
              key={index}
              style={{
                ...style,
              }}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant={'body2'}>{label}</Typography>
              <Typography variant={'body2'} style={{ fontWeight: 'bold', color: '#2c3e50' }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      </>
    );
  };

  const mergePolygons = (): turf.Feature<turf.Polygon> | undefined => {
    if (selectedFields.length < 2) {
      submitButton.current = { disable: true, reason: '' };
      return;
    }

    const fieldAPolygon = turf.polygon(selectedFields[0].polygon.coordinates);
    const fieldBPolygon = turf.polygon(selectedFields[1].polygon.coordinates);
    let mergedPolygon = turf.union(fieldAPolygon, fieldBPolygon);

    // if fields are not intersecting, create a buffer around the fields and see if they intersect
    if (mergedPolygon.geometry.type === 'MultiPolygon') {
      mergedPolygon = turf.buffer(
        turf.union(
          turf.buffer(fieldAPolygon, 6, { units: 'meters' }),
          turf.buffer(fieldBPolygon, 6, { units: 'meters' })
        ),
        -6,
        { units: 'meters' }
      );

      if (mergedPolygon.geometry.type === 'MultiPolygon') {
        submitButton.current = {
          disable: true,
          reason: LangFile.MergeFieldConfirmationPopover.disableReason.tooFarAway,
        };
        return;
      }
    }

    submitButton.current = { disable: false, reason: '' };

    mergedPolygon = turf.simplify(mergedPolygon, { tolerance: 0.00001, highQuality: true });
    return mergedPolygon as turf.Feature<turf.Polygon>;
  };

  const goToOverviewViewMode: () => void = useCallback((): void => {
    dispatch(setViewMode(ViewModeConstants.EDIT_FIELD));
  }, []);

  const mergedFieldsPolygon = useMemo(() => mergePolygons(), [selectedFields]);

  const renderFieldText = useCallback(
    (field, zoom) => {
      let lines = [];
      if (zoom >= 15) {
        lines.push({
          text: getFieldName(field, LangFile),
          fontWeight: '400',
          fontSize: 14,
          textAlign: 'center',
          color: 'white',
        });
      }
      return lines;
    },
    [fields]
  );

  const renderConfirmActionPopover = () => {
    return (
      <ConfirmActionPopover
        shown={true}
        LangFile={LangFile}
        action={() => {
          setShowMergeFieldsInputDialog(true);
        }}
        title={LangFile.MergeFieldConfirmationPopover.title}
        message={LangFile.MergeFieldConfirmationPopover.message}
        actionText={LangFile.MergeFieldConfirmationPopover.mergeText}
        cancelText={LangFile.MergeFieldConfirmationPopover.cancelText}
        cancelAction={goToOverviewViewMode}
        disabled={submitButton.current.disable}
        disabledReason={submitButton.current.reason}
        status={renderStatus()}
        style={{ marginTop: '30px' }}
        helpIcon={<HelpItemContainer type={'icon'} articleId={MERGE_FIELDS_HELP_ARTICLE} />}
      />
    );
  };

  const renderMergeFieldsInputDialog = () => {
    return (
      <MergeFieldsInputDialog
        open={showMergeFieldsInputDialog}
        inputs={{
          fieldName: mergedFields.fieldName,
          fieldNumber: mergedFields.fieldNumber,
        }}
        onChange={(key, value) => {
          dispatch(
            setIsEditingMergedField({
              mergedFields: {
                ...mergedFields,
                [key]: value,
              },
            })
          );
        }}
        onClose={() => setShowMergeFieldsInputDialog(false)}
        onConfirm={() => {
          dispatch(
            setIsEditingMergedField({
              isEditingMergedField: true,
              mergedFields: {
                fieldA: selectedFields[0],
                fieldB: selectedFields[1],
                newCoordinates: mergedFieldsPolygon.geometry.coordinates[0],
                fieldName: mergedFields.fieldName || '',
                fieldNumber: mergedFields.fieldNumber,
              },
            })
          );
        }}
      />
    );
  };

  return (
    <>
      {fieldMapPolygons}
      {!isEditingMergedField && (
        <FieldTextCanvas fields={fieldsArray} renderField={renderFieldText} />
      )}
      {mergedFieldsPolygon && !isEditingMergedField && (
        <MapPolygon
          fillOpacity={0.0}
          fillColor={'white'}
          strokeColor={'#E53935'}
          strokeWeight={4}
          disabled={true}
          coordinates={mergedFieldsPolygon.geometry.coordinates[0].map(
            (coord: Coordinate): CoordinateObject => {
              return {
                lng: coord[0],
                lat: coord[1],
              };
            }
          )}
        />
      )}
      {!isEditingMergedField && (
        <>
          {renderConfirmActionPopover()}
          {renderMergeFieldsInputDialog()}
        </>
      )}
    </>
  );
};

const connector = connect(mapStoreToProps);
export default memo(connector(MergeFields));
